/**
 * CredentialFields — the identifier and the secret a person signs in with (module-private to
 * `<LoginForm>`).
 *
 * LOGIN-CONVERGENCE W8 (2026-10-11): the credential is DESCRIBED, never assumed to be an email and a
 * password. A surface passes `credentialFields`:
 *
 *   { identifier: FieldDescriptor | null, secret: FieldDescriptor }
 *   FieldDescriptor = { label?, type?, inputMode?, autoComplete?, maxLength? }
 *
 * - `identifier` omitted → the email field (`type="email"`, `autoComplete="email"`);
 *   `identifier: null` → a SINGLE-CODE sign-in (an access code): no identifier at all.
 * - `secret` omitted → the password field. The secret is always masked (`type="password"`) with the
 *   show/hide toggle — `@puffin-core/auth-shell`'s ONE `<PasswordField>` (PALM-PHONE-UAT-001 A6),
 *   controlled by the form's own `showPassword` / `togglePassword`; `inputMode` / `maxLength` shape
 *   a PIN through its `inputProps`.
 * - `label` is a sentence the surface has already translated; absent, the catalog's.
 *
 * The element ids stay `email` / `password` whatever the labels say, so a page's labels, its tests
 * and its password managers find the same two fields on every sign-in in the fleet. W21: a sign-in
 * rendered OVER a page (a re-authentication dialog) names its own with the descriptor's `id`, because
 * the page behind it may already hold an `#email` — a duplicate id points the dialog's label at the
 * page's field, outside the dialog's focus trap.
 *
 * W11 + W13: while the form shows a word about the last attempt, or is locked by the throttle, both
 * fields are `aria-invalid` and are described by that alert and/or the throttle notice, so a screen
 * reader reads the refusal with the field it is about.
 *
 * W14: a descriptor may also carry `minLength`, `pattern` (a RegExp source) and `invalidMessage` (a
 * translated sentence). They are the FORM's check (`useLoginForm` validates before anything is sent),
 * never native attributes: a native `pattern` / `minlength` would pre-empt the handler with the
 * browser's own bubble, which FVA-001 forbids. Only `maxLength` is rendered natively — it stops
 * typing, and refuses nothing.
 *
 * @module @puffin-core/login-shell/components/loginForm/CredentialFields
 */

import PropTypes from 'prop-types';
import { PasswordField } from '@puffin-core/auth-shell';
import { describingAlertIds } from './SignInAlerts.jsx';

/** The shape both descriptors share. */
/**
 * One credential field's description, as `fieldDescriptorShape` checks it at run time.
 *
 * @typedef {Object} CredentialFieldDescriptor
 * @property {string} [label]
 * @property {string} [type]
 * @property {string} [inputMode]
 * @property {string} [autoComplete]
 * @property {number} [maxLength]
 * @property {string} [id] - the field's element id (W21)
 * @property {string} [value] - a LOCKED identifier's value (W18)
 * @property {boolean} [readOnly]
 * @property {number} [minLength] - the form's own check (W14)
 * @property {string} [pattern]
 * @property {string} [invalidMessage]
 */
export const fieldDescriptorShape = PropTypes.shape({
  label: PropTypes.string,
  type: PropTypes.string,
  inputMode: PropTypes.string,
  autoComplete: PropTypes.string,
  maxLength: PropTypes.number,
  // W21 — the field's element id, for a sign-in rendered over a page that may hold its own `#email`.
  id: PropTypes.string,
  // W18 — a LOCKED identifier: `value` is shown and signed in as; `readOnly` keeps it so.
  value: PropTypes.string,
  readOnly: PropTypes.bool,
  // W14 — the form's own check (never rendered natively).
  minLength: PropTypes.number,
  pattern: PropTypes.string,
  invalidMessage: PropTypes.string,
});

/**
 * @param {Object} props
 * @param {Object} props.form - `useLoginForm`'s state.
 * @param {(key: string) => string} props.translate
 * @param {Object|null} [props.identifier] - the identifier descriptor; `null` for a single code.
 * @param {Object} [props.secret] - the secret descriptor.
 * @returns {React.JSX.Element}
 */
export function CredentialFields({ form, translate, identifier, secret = {} }) {
  const identifierId = identifier?.id ?? 'email';
  const secretId = secret.id ?? 'password';
  const describedBy = describingAlertIds(form);
  const invalidProps =
    describedBy.length > 0
      ? { 'aria-invalid': 'true', 'aria-describedby': describedBy.join(' ') }
      : {};
  return (
    <>
      {identifier !== null && (
        <div className="form-group">
          <label className="form-label" htmlFor={identifierId}>
            {identifier?.label ?? translate('login.email.label')}
          </label>
          <input
            className="form-input"
            id={identifierId}
            type={identifier?.type ?? 'email'}
            inputMode={identifier?.inputMode}
            maxLength={identifier?.maxLength}
            value={form.email}
            readOnly={identifier?.readOnly === true}
            onChange={form.handleEmailChange}
            aria-required="true"
            {...invalidProps}
            autoComplete={identifier?.autoComplete ?? 'email'}
            disabled={form.isLoading}
          />
        </div>
      )}

      {/* PALM-PHONE-UAT-001 A6: the ONE password field. The form holds the shown / hidden state (it is
          the login state machine's), so the field is controlled; a sign-in marks every control
          required and no label, so `aria-required` rides `inputProps`, as the identifier's does. */}
      <PasswordField
        id={secretId}
        label={secret.label ?? translate('login.password.label')}
        value={form.password}
        onChange={form.handlePasswordChange}
        autoComplete={secret.autoComplete ?? 'current-password'}
        disabled={form.isLoading}
        shown={Boolean(form.showPassword)}
        onToggleShown={form.togglePassword}
        inputProps={{
          inputMode: secret.inputMode,
          maxLength: secret.maxLength,
          'aria-required': 'true',
          ...invalidProps,
        }}
      />
    </>
  );
}

CredentialFields.propTypes = {
  form: PropTypes.shape({
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    showPassword: PropTypes.bool,
    isLoading: PropTypes.bool,
    handleEmailChange: PropTypes.func.isRequired,
    handlePasswordChange: PropTypes.func.isRequired,
    togglePassword: PropTypes.func.isRequired,
    refusal: PropTypes.object,
    error: PropTypes.string,
    rateLimit: PropTypes.object,
  }).isRequired,
  translate: PropTypes.func.isRequired,
  identifier: fieldDescriptorShape,
  secret: fieldDescriptorShape,
};

export default CredentialFields;
